import { AssignedHours, AssignmentOperation, RecordUserAssignment } from "@/types/assignment"

import apiAssignment from "@/services/api/api_assignment"
import { CaseDetails, CaseField, Field } from "@/types"
import { UserRole } from "@/types/caseUser"
import { TenantFeatures } from "@/types/tenantFeatures"
import Vue from "vue"
import { FieldTypeId } from "@/types/fieldTypes"
import { AssignmentMergeType, TimeManagementSettings } from "@/types/timeManagement"

/**
 * Class for managing assignments
 */
export default class RecordAssignmentService {
	private record: CaseDetails | null = null

	private _assignments: RecordUserAssignment[] = []

	public userRolesWithHours: CaseField[] = []
	public fieldsByHours: { [key: string]: CaseField[] } = {}
	public hourFieldAssignments: { [key: string]: AssignedHours } = {}

	private getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	constructor (getIsFeatureEnabled: ((feature: TenantFeatures) => boolean)) {
		this.getIsFeatureEnabled = getIsFeatureEnabled
	}

	/**
	 * Get Assignments for current record
	 */
	public get assignments (): RecordUserAssignment[] {
		return this._assignments
	}

	/**
	 * Set Assignments for current record
	 */
	public set assignments (value: RecordUserAssignment[]) {
		this._assignments = value
		this.updateValues()
	}

	/**
	 * Load assignments for record
	 * @param {CaseDetails} record - details of record to load assignments
	 */
	public async load (record: CaseDetails, getIsFeatureEnabled: ((feature: TenantFeatures) => boolean) | null = null) {
		this.assignments = []

		this.record = record
		this.fieldsByHours = {}
		this.hourFieldAssignments = {}

		if (getIsFeatureEnabled !== null) {
			this.getIsFeatureEnabled = getIsFeatureEnabled
		}
		const assignments = await apiAssignment.recordAssignments(record.id!)
		this.assignments = assignments
	}

	/**
	 * Clear assignments and record data
	 */
	public clear () {
		this.assignments = []

		this.record = null
		this.fieldsByHours = {}
		this.hourFieldAssignments = {}
	}

	/**
	 * Retrieves the assignment date field from the record.
	 *
	 * Priority is given to the field-level assignment date (if defined in the field's extensions).
	 * If a field-level ID is defined but not present on the record, returns null.
	 * If no field-level ID is defined, attempts to use the fallback from time management settings.
	 * If the fallback ID is not present on the record, returns null.
	 *
	 * @param field - The user-role field.
	 * @param timeManagementSettings - Time management settings that may define a fallback assignment date field ID.
	 * @returns The matching CaseField assignment date from the record, or null if none is found.
	 */
	public getAssignmentDate (field: Field, timeManagementSettings: TimeManagementSettings): CaseField | null {
		const fieldLevelAssignmentDateId = field.extensions?.timeManagementAssignmentDate
		const fallbackAssignmentDateId = timeManagementSettings?.assignmentDateFieldId

		if (fieldLevelAssignmentDateId != null) {
			return this.record?.fields?.[fieldLevelAssignmentDateId] ?? null
		}

		if (fallbackAssignmentDateId != null) {
			return this.record?.fields?.[fallbackAssignmentDateId] ?? null
		}

		return null
	}

	/**
	 * Assigns a given user to the current record for a given date and field id
	 * @param userId User ID to assign to
	 * @param fieldId Field ID to assign the user to
	 * @param date Date of assignment
	 * @param hours Number of hours to assign
	 * @param operation Operation - Set sets hours on given date, NextDay sets hours on next available date, AutoFill fills rest of hours on subsequent dates
	 */
	public async assign (userId: string, fieldId: string, date: string, hours: number, operation: AssignmentOperation = AssignmentOperation.Set) {
		if (this.record && this.record.id) {
			this.assignments = await apiAssignment.assignmentSet(this.record.id, userId, fieldId, date, hours, operation)
		}
	}

	/**
	 * Unassigns a given user to the current record for a given date and field id
	 * @param userId User ID to unassign
	 * @param fieldId Field ID to unassign
	 * @param date Date of assignment to remove
	 */
	public async remove (userId: string, fieldId: string, date: string) {
		if (this.record && this.record.id) {
			this.assignments = await apiAssignment.assignmentRemove(this.record.id, userId, fieldId, date)
		}
	}

	/**
	 * Removes assignments from this.assignments that no longer exist on a record.
	 * @param caseDetails The details of the case containing the fields and roles.
	 */
	public removeInvalidAssignments (caseDetails: CaseDetails) {
		for (let i = this.assignments.length - 1; i >= 0; i--) {
			const assignment = this.assignments[i]
			const field = caseDetails!.fields[assignment.fieldId]?.value as UserRole

			if (field) {
				let isInSubRoles = false

				if (field.subRoles) {
					const subRoles = field.subRoles.flatMap(role => role.users ?? [])
					isInSubRoles = subRoles.some(user => user.id === assignment.userId)
					if (!isInSubRoles) {
						const indexToRemove = this.assignments.findIndex(a =>
							a.userId === assignment.userId && a.fieldId === assignment.fieldId
						)
						if (indexToRemove !== -1) {
							this.assignments.splice(indexToRemove, 1)
						}
					}
				}

				if (!isInSubRoles && field.users) {
					const isInRegularUsers = field.users.some(user => user.id === assignment.userId)
					if (!isInRegularUsers) {
						const indexToRemove = this.assignments.findIndex(a =>
							a.userId === assignment.userId && a.fieldId === assignment.fieldId
						)
						if (indexToRemove !== -1) {
							this.assignments.splice(indexToRemove, 1)
						}
					}
				}
			}
		}
		this.updateValues()
	}

	/**
	 * Adds or updates this.assignments with new assignments.
	 * @param newAssignments The new list of assignments to merge.
	 */
	public mergeAssignments (newAssignments: RecordUserAssignment[],
		mergeType: AssignmentMergeType.Merge | AssignmentMergeType.ReplaceForFields
	) {
		const mergeRoleIds = new Set(newAssignments.map(a => a.fieldId))

		const updatedAssignments: RecordUserAssignment[] = []

		for (let newAssignment of newAssignments) {
			const assignmentIndex = this.assignments.findIndex((assignment) =>
				assignment.userId === newAssignment.userId &&
				assignment.fieldId === newAssignment.fieldId &&
				assignment.date === newAssignment.date
			)

			if (assignmentIndex !== -1) {
				Vue.set(this.assignments, assignmentIndex, newAssignment)
			} else {
				this.assignments.push(newAssignment)
			}

			updatedAssignments.push(newAssignment)
		}

		if (mergeType === AssignmentMergeType.ReplaceForFields) {
			// Remove assignments for the affected Fields that no longer exist.
			this.assignments = this.assignments.filter(a =>
				!mergeRoleIds.has(a.fieldId) || updatedAssignments.includes(a)
			)
		}

		this.updateValues()
	}

	/**
	 * If a field value has changed that would affect assignment hours, then update the values
	 * @param oldFields Values of fields before update
	 */
	public updateHoursIfChanged (oldFields: { [fieldId: string]: CaseField }) {
		// Temporary implementation, see comment below
		if (this.fieldsWithHours(oldFields).length > 0) {
			this.updateValues()
		}

		// TODO: This method needs fixing, as the existing checks are incorrect and the method was already
		// running on every Field change, except some instances where it wasn't running when it should have been.
		// Issues:
		// - You cannot check if a user role field has changed by comparing the old and new value objects, as they will always be different.
		// - There is no check for whether an hours field has changed, which is another type of change that should trigger an update.

		// const oldFieldsValues = this.fieldsWithHours(oldFields).toDictionaryMap(f => f.field.id!)
		// if (this.userRolesWithHours.some(f => !oldFieldsValues[f.field.id!] || oldFieldsValues[f.field.id!].value !== f.value)) {
		// 	this.updateValues()
		// }
	}

	/**
	 * @summary Gets the hours Field ID that a user role should use on a given record.
	 * @param hoursFieldIds The list of hours Field IDs, ordered by priority.
	 * @param record The record to get the appropriate hours Field ID for.
	 * @returns The hours Field ID that should be used for the given record, or null if none are found.
	 */
	public getTimeManagementHoursForRecord (field: Field, record: CaseDetails): string | null {
		if (field.dataType !== FieldTypeId.UserRole) return null

		const roleHoursFieldIds = this.getTimeManagementHoursListFromRoleField(field)
		return roleHoursFieldIds.firstOrDefault(hoursFieldId => !!record.fields[hoursFieldId]) ?? null
	}

	/**
	 * Gets a list of all configured hours Field IDs for a user role Field.
	 * Note: A user role Field only uses one hours Field per record.
	 * @param field The user role Field to get the hours Field IDs for.
	 * @returns A list of all configured hours Field IDs for the user role Field.
	 */
	public getTimeManagementHoursListFromRoleField (field: Field | undefined): string[] {
		const fieldExtensions = field?.extensions
		if (field?.dataType !== FieldTypeId.UserRole || !fieldExtensions?.timeManagementHours) {
			return []
		}

		const timeManagementHours = fieldExtensions.timeManagementHours

		if (typeof timeManagementHours === "string") {
			return [timeManagementHours]
		} else if (Array.isArray(timeManagementHours)) {
			return this.getIsFeatureEnabled(TenantFeatures.AllowMultipleHourFieldsForTimeManagementEnabledUserRole)
				? timeManagementHours
				: timeManagementHours.slice(0, 1)
		}

		return []
	}

	private updateValues () {
		if (this.record) {
			this.userRolesWithHours = this.fieldsWithHours(this.record.fields)

			this.fieldsByHours = this.userRolesWithHours.reduce((group, field) => {
				const roleHoursFieldIdForRecord = this.getTimeManagementHoursForRecord(field.field, this.record!)
				if (!roleHoursFieldIdForRecord) {
					console.error("No valid hours field found for user role:", field)
					return group
				}

				if (!group[roleHoursFieldIdForRecord]) {
					group[roleHoursFieldIdForRecord] = [field]
				} else {
					group[roleHoursFieldIdForRecord].push(field)
				}
				return group
			}, {} as { [key: string]: CaseField[] })

			const hourAssignedTime: AssignedHours[] = Object.entries(this.fieldsByHours).map(kv => ({
				hoursFieldId: kv[0],
				userRoleFields: kv[1],
				hours: this.record?.fields[kv[0]]?.value ?? 0,
				assignedTime: this.assignments.filter(a => kv[1].some(f => f.field.id === a.fieldId)).sum("hours"),
				unassignedTime: 0
			}))

			hourAssignedTime.forEach(t => t.unassignedTime = t.hours - t.assignedTime)
			this.hourFieldAssignments = hourAssignedTime.toDictionary("hoursFieldId")
		}
	}

	private fieldsWithHours (recordFields: { [fieldId: string]: CaseField }): CaseField[] {
		if (!recordFields) return []

		return Object.values(recordFields).filter(f => f.field.dataType === "user-role" &&
			this.getTimeManagementHoursListFromRoleField(f.field)
				.some((hoursFieldId: string) => recordFields[hoursFieldId])
		)
	}
}
